import {
  useMutation,
  UseQueryResult,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import client, { moreMemoryApiClient } from "../client";
import {
  AccessCardHistory,
  AccessCardOrderType,
  AccessBadge,
  AccessRole,
  CardOrderedBy,
  CardType,
} from "@/types";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

export const allAccessCardOptions = (
  isAdmin: boolean,
  requestsOnly?: boolean,
  disabledPlasticCardOrdering?: boolean,
) => {
  return {
    queryKey: ["all-access-cards", requestsOnly],
    queryFn: async () => {
      if (!isAdmin || disabledPlasticCardOrdering) {
        return [];
      }
      try {
        const response = await client.get(
          `/getAccessCards?requestsOnly=${requestsOnly}`,
        );
        return response.data.requests;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: isAdmin && !disabledPlasticCardOrdering,
  };
};

export const useAllAccessCards = (
  isAdmin: boolean = false,
  requestsOnly?: boolean,
  disabledPlasticCardOrdering?: boolean,
): UseSuspenseQueryResult<AccessCardOrderType[]> => {
  return useBuildingSuspenseQuery(
    allAccessCardOptions(isAdmin, requestsOnly, disabledPlasticCardOrdering),
  );
};

export const useAccessCardHistory = (
  cardId: string,
  userId: string,
): UseQueryResult<AccessCardHistory[]> => {
  return useBuildingQuery({
    queryKey: ["access-card-history", cardId, userId],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getAccessCardHistoryById?cardId=${cardId}&userId=${userId}`,
        );
        return response.data.history;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
  });
};

export const useFrozenCardsForBuilding = (
  buildingIntegratedWithAcs: boolean = false,
  isBuildingAdmin: boolean = false,
): UseQueryResult<AccessBadge[]> => {
  return useBuildingQuery({
    queryKey: ["frozen-access-cards"],
    queryFn: async () => {
      try {
        const response = await client.get("/getAllFrozenAccessCards");
        return response.data.accessCards;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: !!buildingIntegratedWithAcs && isBuildingAdmin,
  });
};

// Access roles implementation is commented out until Sharry provides us with the necessary API endpoints
// export const useAllAccessRolesForCompanyUsers = (
//   companyId: string | undefined,
//   accessControlIntegrated = false,
// ): UseQueryResult<AccessRole[]> => {
//   return useBuildingQuery({
//     queryKey: ["company-users-access-roles", companyId],
//     queryFn: async () => {
//       try {
//         const response = await client.get(`/getAllAccessRolesForCompanyUsers`, {
//           params: { companyId },
//         });
//         return response.data.userAccessRoles;
//       } catch (error) {
//         console.error(error);
//         return [];
//       }
//     },
//     enabled: !!companyId && accessControlIntegrated,
//   });
// };

export const createOrCompleteAccessCardOrder = async (
  type: CardType,
  orderedBy: CardOrderedBy,
  userId: string,
  companyId: string,
  orderNote?: string,
  completed: boolean = false,
  cardNumber?: string,
) => {
  return await client.post("/createAccessCard", {
    type,
    orderedBy,
    userId,
    orderNote,
    companyId,
    completed,
    cardNumber,
  });
};

export const updateAccessCard = async (
  userId: string,
  cardId: string,
  updateData: Partial<AccessCardOrderType>,
  companyId?: string,
) => {
  return await client.patch("/updateAccessCard", {
    userId,
    cardId,
    updateData,
    companyId,
  });
};

export const resendAccessCardPickupEmail = async (cardId: string) => {
  return await client.post("/resendAccessCardPickupEmail", {
    cardId,
  });
};

export const updateAccessCardNote = async (
  userId: string,
  cardId: string,
  note: string,
) => {
  return await client.patch("/updateCardNote", {
    userId,
    cardId,
    note,
  });
};

export const reactivateAccessCard = async (
  cardHolderId: string,
  accessCardId: string,
  buildingTenantId: string,
) => {
  return await client.patch("/reactivateAccessCard", {
    cardHolderId,
    accessCardId,
    buildingTenantId,
  });
};

export const deleteAccessCardInAcs = async (
  accessCardId: string,
  userName: string,
  userEmail: string,
  cardNumber: string,
  buildingTenantId: string,
  userId?: string,
) => {
  return await client.delete("/deleteAccessCard", {
    data: {
      accessCardId,
      userName,
      userEmail,
      cardNumber,
      buildingTenantId,
      userId,
    },
  });
};

export const accessCardsDetailsOptions = (
  userId: string,
  integratedWithAccessControl = false,
) => {
  return {
    queryKey: ["access-cards-integrated", userId],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getUserAccessCards?userId=${userId}`,
        );
        return response.data.accessCards;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: integratedWithAccessControl,
  };
};

export const useAccessCardsDetails = (
  userId: string,
  integratedWithAccessControl = false,
): UseQueryResult<AccessBadge[]> => {
  return useBuildingQuery(
    accessCardsDetailsOptions(userId, integratedWithAccessControl),
  );
};

export const accessRolesDetailsOptions = (
  userId: string,
  integratedWithAccessControl = false,
  companyId: string,
) => {
  return {
    queryKey: ["access-roles", userId, companyId],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getUserAccessRoles?userId=${userId}&companyId=${companyId}`,
        );
        return response.data.roles;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: integratedWithAccessControl,
  };
};

export const useAccessRolesDetails = (
  userId: string,
  integratedWithAccessControl = false,
  companyId: string,
): UseQueryResult<AccessRole[]> => {
  return useBuildingQuery(
    accessRolesDetailsOptions(userId, integratedWithAccessControl, companyId),
  );
};

export const useCompanyAccessRoles = (
  companyId: string,
  enabled: boolean = true,
): UseQueryResult<AccessRole[]> => {
  return useBuildingQuery({
    queryKey: ["company-access-roles", companyId],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getCompanyAccessRoles?companyId=${companyId}`,
        );
        return response.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: !!companyId && enabled,
  });
};

export const useCompanyVisitorsAccessRoles = (
  companyId: string,
  enabled: boolean = true,
): UseQueryResult<AccessRole[]> => {
  return useBuildingQuery({
    queryKey: ["company-visitors-access-roles", companyId],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getCompanyVisitorsAccessRoles?companyId=${companyId}`,
        );
        return response.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: !!companyId && enabled,
  });
};

export const createUserInAccessControlSystem = async (
  email: string,
  firstName: string,
  lastName: string,
  userId: string,
  companyId: string,
  companyName: string,
  acsDigitalBadgeMode = false,
  sendDigitalAccessEmail: boolean = false,
  language: string,
  companyContact: string,
  propertyOwner: string | undefined,
  imageUrl?: string,
) => {
  return await moreMemoryApiClient.post("/createUserInAccessControlSystem", {
    email,
    firstName,
    lastName,
    userId,
    companyId,
    companyName,
    acsDigitalBadgeMode,
    sendDigitalAccessEmail,
    language,
    companyContact,
    propertyOwner,
    imageUrl,
  });
};

export const activeAccessCardNumbersOptions = (companyId: string) => {
  return {
    queryKey: ["active-access-cards", companyId],
    queryFn: async () => {
      try {
        const response = await moreMemoryApiClient.get(
          `/getActiveAccessCardsForCompanyUsers?companyId=${companyId}`,
        );
        if (response.status === 204) {
          return [];
        }
        return response.data.userActiveAccessCardNumbers;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
  };
};

export const changeUserPinCode = async (userId: string, pinCode: string) => {
  return await client.patch("/changePinCode", {
    userId,
    pinCode,
  });
};

export const resetUserPinCodeByAdmin = async (
  userId: string,
  pinCode: string,
  companyId?: string,
  sendEmail = true,
) => {
  return await client.patch("/resetUserPinCodeByAdmin", {
    userId,
    pinCode,
    companyId,
    sendEmail,
  });
};

export const updateAccessRolesForUser = async (
  userId: string,
  buildingTenantId: string,
  rolesToAdd: AccessRole[] | undefined,
  rolesToDelete: AccessRole[] | undefined,
) => {
  return await client.patch("/updateUserAccessRoles", {
    userId,
    buildingTenantId,
    rolesToAdd,
    rolesToDelete,
  });
};

export const createCompanyInAccessControlSystem = async (
  companyId: string,
  companyName: string,
  owner: string | undefined,
  organizationNumber: string,
) => {
  return await client.post("/createCompanyInAccessControlSystem", {
    companyId,
    companyName,
    owner,
    organizationNumber,
  });
};

export const blockAccessCardInAcs = async (
  userId: string,
  accessCardId: string,
  userName: string,
  userEmail: string,
  cardNumber: string,
  buildingTenantId: string,
) => {
  return await client.patch("/blockAccessCard", {
    userId,
    accessCardId,
    userName,
    userEmail,
    cardNumber,
    buildingTenantId,
  });
};

type GenerateCodePayload = {
  userId: string;
  userName: string;
  userEmail: string;
  userLanguage: string;
};

type GenerateCodeResponse = {
  accessCode: string;
};

export const useGenerateAccessCode = () => {
  return useMutation<GenerateCodeResponse, Error, GenerateCodePayload>({
    mutationFn: async ({
      userId,
      userName,
      userEmail,
      userLanguage,
    }: GenerateCodePayload) => {
      const response = await client.post("/generateAccessCodeForUser", {
        userId,
        userName,
        userEmail,
        userLanguage,
      });
      return response.data;
    },
  });
};

export const getOrderedAccessCardsByUserId = async (userId: string) => {
  return await client.get(`/getOrderedAccessCardsByUserId?userId=${userId}`);
};

export const useOrderedAccessCards = (
  userId: string,
  integratedWithAccessControl = false,
): UseQueryResult<AccessCardOrderType[]> => {
  return useBuildingQuery({
    queryKey: ["ordered-access-cards", userId, integratedWithAccessControl],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getOrderedAccessCardsByUserId?userId=${userId}`,
        );
        return response.data.cards;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: integratedWithAccessControl,
  });
};

export const useUsersWithPendingAccessCardOrders = (
  userIds: string[] | undefined,
  companyId: string,
  enabled = true,
): UseQueryResult<string[]> => {
  return useBuildingQuery({
    queryKey: [
      "users-with-pending-access-cards",
      userIds?.slice().sort().join(","),
      companyId,
    ],
    enabled: enabled && Array.isArray(userIds) && userIds.length > 0,
    queryFn: async () => {
      try {
        const response = await client.get(
          "/getUsersWithPendingAccessCardOrders",
          { params: { companyId, userIds } },
        );
        return response.data.userIds as string[];
      } catch (error) {
        console.error("Failed to fetch pending access card users:", error);
        return [];
      }
    },
  });
};

export const createCardInAcs = async (
  userId: string,
  accessCardNumber: string,
  companyId: string,
  cardFormatId?: string,
) => {
  return await client.post("/createAccessCardInAcs", {
    userId,
    accessCardNumber,
    companyId,
    cardFormatId,
  });
};

export interface AccessCardFormat {
  id: string;
  name: string;
}

export const accessCardFormatsOptions = (
  userId: string,
  companyId: string,
) => ({
  queryKey: ["access-card-formats", userId, companyId],
  queryFn: async (): Promise<AccessCardFormat[]> => {
    const response = await client.get("/accessCardFormats", {
      params: { userId, companyId },
    });
    return response.data.formats ?? [];
  },
});

export const sendDigitalAccessEmailToUser = async (
  email: string,
  firstName: string,
  companyContact: string,
  buildingTenantId: string,
  language: string,
) => {
  return await client.post("/sendDigitalAccessEmail", {
    email,
    firstName,
    companyContact,
    buildingTenantId,
    language,
  });
};

export const sendNewAccessCardEmailToUser = async (userId: string) => {
  return await client.post("/sendNewAccessCardEmail", {
    userId,
  });
};

export const deleteUserOrRemoveLocationFromAcs = async (
  userId: string,
  email: string,
) => {
  return await client.delete("/deleteUserOrRemoveLocationAcs", {
    data: { userId, email },
  });
};
